/**
 * Recipient problems worth telling the adviser about on an envelope that is
 * still with DocuSign. Pure — the detail modal, the Docusign page and the
 * workflow task Envelopes tab all read the same answers.
 *
 * - **Bounced:** DocuSign marks a recipient `autoresponded` when its signing
 *   email bounced or came back with an automatic reply, so that person has no
 *   working link to sign.
 * - **Not the Fact Find email:** the client signer's address on a sent
 *   envelope differs from `personal.email` — usually after someone used
 *   DocuSign's Correct, which stops every link sent to the old address.
 */

export const BOUNCED_RECIPIENT_STATUS = 'autoresponded';

/** Statuses where a bounce or a changed address still matters. */
const BOUNCE_STATUSES = new Set(['tagging', 'sent']);

export function isCcRecipient(recipient) {
  return recipient?.deliveryRole === 'carbonCopy' || recipient?.role === 'CC' || recipient?.label === 'CC';
}

export function isClientSignerRecipient(recipient) {
  return recipient?.id === 'client' || recipient?.label === 'Client' || recipient?.role === 'Client';
}

export function isRecipientBounced(recipient) {
  return String(recipient?.status || '').toLowerCase() === BOUNCED_RECIPIENT_STATUS;
}

export function normaliseEmail(email) {
  return String(email || '').trim().toLowerCase();
}

/** Signers whose signing email bounced, while the envelope is still out for signing. */
export function bouncedSignersOf(envelope) {
  if (!BOUNCE_STATUSES.has(envelope?.status)) return [];
  return (envelope?.recipients || []).filter((r) => !isCcRecipient(r) && isRecipientBounced(r));
}

/** The client's live signer row (null before DocuSign reports recipients). */
export function clientSignerOf(envelope) {
  return (envelope?.recipients || []).find((r) => !isCcRecipient(r) && isClientSignerRecipient(r)) || null;
}

/**
 * The client signer's address when it is not the Fact Find email, on a sent
 * envelope. Null when either address is missing or they match (case-insensitive).
 * @returns {{ signerEmail: string, factFindEmail: string } | null}
 */
export function clientEmailMismatchOf(envelope, factFindEmail) {
  if (envelope?.status !== 'sent') return null;
  const signer = clientSignerOf(envelope);
  const signerEmail = String(signer?.email || '').trim();
  const fileEmail = String(factFindEmail || '').trim();
  if (!signerEmail || !fileEmail) return null;
  if (normaliseEmail(signerEmail) === normaliseEmail(fileEmail)) return null;
  return { signerEmail, factFindEmail: fileEmail };
}

/** "Name (email)", or whichever of the two is known. */
export function recipientLabel(recipient) {
  const name = String(recipient?.name || '').trim();
  const email = String(recipient?.email || '').trim();
  if (name && email) return `${name} (${email})`;
  return name || email || 'Recipient';
}

/**
 * Short lines for list rows (Docusign page status cell, workflow task
 * Envelopes tab): `tone` is 'destructive' for a bounce, 'warning' for an
 * address that is not the Fact Find email.
 * @returns {Array<{ id: string, tone: 'destructive'|'warning', label: string, title: string }>}
 */
export function envelopeRecipientAlertLines(envelope, factFindEmail) {
  const lines = [];
  const bounced = bouncedSignersOf(envelope);
  if (bounced.length) {
    lines.push({
      id: 'email-bounced',
      tone: 'destructive',
      label: 'Email bounced',
      title: `DocuSign's signing email bounced for ${bounced.map(recipientLabel).join(', ')}. Open the envelope for what to do next.`,
    });
  }
  const mismatch = clientEmailMismatchOf(envelope, factFindEmail);
  if (mismatch) {
    lines.push({
      id: 'not-fact-find-email',
      tone: 'warning',
      label: 'Not the Fact Find email',
      title: `Signing email ${mismatch.signerEmail} is not the Fact Find email ${mismatch.factFindEmail}.`,
    });
  }
  return lines;
}
